(() => {
  const root = document.querySelector('[data-quick-purchase]');
  if (!root) return;

  const rows = root.querySelector('[data-quick-purchase-rows]');
  const addRowButton = root.querySelector('[data-add-row]');
  const addToCartButton = root.querySelector('[data-add-to-cart]');
  const message = root.querySelector('[data-message]');
  const maxSuggestions = 8;
  const searchUrl = root.dataset.searchUrl;
  const orderMap = window.quickPurchaseOrder || {};
  const skuCollator = new Intl.Collator('ja', { numeric: true, sensitivity: 'base' });
  const resetAfterCartKey = 'quickPurchaseResetAfterCart';
  let activeSearchController;

  window.addEventListener('pageshow', () => {
    if (sessionStorage.getItem(resetAfterCartKey) === '1') {
      sessionStorage.removeItem(resetAfterCartKey);
      window.location.reload();
    }
  });

  const normalize = (value) => String(value || '').normalize('NFKC').toLowerCase().replace(/[\s\-‐－]/g, '');

  const skuForms = (value) => {
    const raw = String(value || '').normalize('NFKC').toLowerCase();
    const tokens = raw.split(/[\s\-‐－]+/).filter(Boolean);
    const full = normalize(raw);
    const withoutStandaloneS = tokens.filter((token) => token !== 's').join('');

    return [...new Set([full, withoutStandaloneS].filter(Boolean))];
  };

  const money = (value) => new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: 'JPY',
    maximumFractionDigits: 0,
  }).format(value / 100);

  const escapeHtml = (value) => String(value || '').replace(
    /[&<>'"]/g,
    (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#039;',
      '"': '&quot;',
    })[character]
  );

  const setMessage = (text, type = '') => {
    message.textContent = text;
    message.className = `quick-purchase__message${type ? ` quick-purchase__message--${type}` : ''}`;
  };

  const rankResults = (products, value) => {
    const query = normalize(value);
    if (!query) return [];

    const uniqueProducts = new Map();

    products.forEach((product) => {
      const key = normalize(product.sku) || product.id;
      const existing = uniqueProducts.get(key);

      if (!existing || (!existing.available && product.available)) {
        uniqueProducts.set(key, product);
      }
    });

    return [...uniqueProducts.values()]
      .map((product) => {
        const forms = skuForms(product.sku);
        let score = 0;

        if (forms.includes(query)) score = 4;
        else if (forms.some((form) => form.startsWith(query))) score = 3;
        else if (forms.some((form) => form.includes(query))) score = 1;

        return {
          product,
          score,
          order: orderMap[normalize(product.sku)] ?? Number.MAX_SAFE_INTEGER,
        };
      })
      .filter((entry) => entry.score)
      .sort((a, b) => (
        b.score - a.score
        || a.order - b.order
        || skuCollator.compare(a.product.sku, b.product.sku)
      ))
      .slice(0, maxSuggestions)
      .map((entry) => entry.product);
  };

  const getSearchTerm = (value) => {
    const baseSkuMatch = String(value || '').match(/^\s*([a-z]+)\s*[-‐－]?\s*(\d+)/i);

    return baseSkuMatch
      ? `${baseSkuMatch[1]}-${baseSkuMatch[2]}`
      : String(value || '');
  };

  const search = async (value) => {
    const rawValue = String(value || '').trim();
    const normalizedValue = rawValue.normalize('NFKC');
    const query = normalize(normalizedValue);

    if (query.length < 2 || !searchUrl) return [];

    if (activeSearchController) activeSearchController.abort();
    activeSearchController = new AbortController();

    const searchTerms = [...new Set([
      getSearchTerm(rawValue),
      getSearchTerm(normalizedValue),
    ].filter(Boolean))];

    try {
      const results = await Promise.all(searchTerms.map(async (term) => {
        const url = new URL(searchUrl, window.location.origin);

        url.searchParams.set('q', term);
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', 'last');
        url.searchParams.set('options[unavailable_products]', 'show');
        url.searchParams.set('view', 'quick-purchase-results');

        const response = await fetch(url.toString(), {
          signal: activeSearchController.signal,
        });

        if (!response.ok) return [];

        const markup = await response.text();
        const documentResult = new DOMParser().parseFromString(markup, 'text/html');

        return [...documentResult.querySelectorAll('[data-quick-purchase-result]')]
          .map((item) => ({
            id: item.dataset.id,
            sku: item.dataset.sku,
            title: item.dataset.title,
            variantTitle: item.dataset.variantTitle,
            color: item.dataset.color,
            price: Number(item.dataset.price),
            available: item.dataset.available === 'true',
          }))
          .filter((item) => item.sku || item.title);
      }));

      return rankResults(results.flat(), normalizedValue);
    } catch (error) {
      if (error.name !== 'AbortError') return [];
      return [];
    }
  };

  const closeSuggestions = (row) => {
    const list = row.querySelector('.quick-purchase__suggestions');
    if (list) list.remove();
  };

  const updatePrice = (row) => {
    const unitPrice = Number(row.dataset.unitPrice);
    const priceElement = row.querySelector('[data-price]');

    if (!Number.isFinite(unitPrice)) {
      priceElement.textContent = '―';
      return;
    }

    const quantity = Math.max(
      1,
      Number(row.querySelector('[data-quantity]').value) || 1
    );

    priceElement.innerHTML = `${money(unitPrice * quantity)}${
      row.dataset.available === 'true'
        ? ''
        : '<span class="quick-purchase__soldout">売り切れ</span>'
    }`;
  };

  const selectProduct = (row, product) => {
    row.dataset.variantId = product.id;
    row.dataset.available = product.available;
    row.dataset.unitPrice = product.price;

    row.querySelector('[data-search-input]').value = product.sku || product.title;

    row.querySelector('[data-product]').innerHTML = `
      <span class="quick-purchase__product-name">${escapeHtml(product.title)}</span>
      <span class="quick-purchase__product-meta">
        ${escapeHtml(product.sku)}
        ${product.color && product.color !== 'Default Title' ? ` ／ ${escapeHtml(product.color)}` : ''}
      </span>
    `;

    updatePrice(row);
    closeSuggestions(row);
  };

  const showSuggestions = (row, matches) => {
    closeSuggestions(row);
    if (!matches.length) return;

    const list = document.createElement('ul');
    list.className = 'quick-purchase__suggestions';

    matches.forEach((product) => {
      const item = document.createElement('li');
      const button = document.createElement('button');

      button.type = 'button';
      button.className = 'quick-purchase__suggestion';

      button.innerHTML = `
        <span class="quick-purchase__suggestion-sku">${escapeHtml(product.sku || '品番未設定')}</span>
        <span class="quick-purchase__suggestion-meta">
          ${escapeHtml(product.title)}
          ${product.color && product.color !== 'Default Title' ? ` ／ ${escapeHtml(product.color)}` : ''}
        </span>
      `;

      button.addEventListener('mousedown', (event) => {
        event.preventDefault();
        selectProduct(row, product);
      });

      item.append(button);
      list.append(item);
    });

    row.querySelector('.quick-purchase__search').append(list);
  };

  const resetRow = (row) => {
    delete row.dataset.variantId;
    delete row.dataset.unitPrice;
    delete row.dataset.available;

    row.querySelector('[data-product]').textContent = '未選択';
    row.querySelector('[data-price]').textContent = '―';
  };

  const createRow = () => {
    const row = document.createElement('tr');

    row.innerHTML = `
      <td class="quick-purchase__search-cell" data-label="品番">
        <div class="quick-purchase__search">
          <input class="quick-purchase__input" data-search-input type="search" autocomplete="off" placeholder="品番を入力">
        </div>
      </td>
      <td class="quick-purchase__product-cell" data-label="選択商品">
        <div class="quick-purchase__product" data-product>未選択</div>
      </td>
      <td class="quick-purchase__price-cell" data-label="小計">
        <div class="quick-purchase__price" data-price>―</div>
      </td>
      <td class="quick-purchase__quantity-cell" data-label="数量">
        <input class="quick-purchase__quantity" data-quantity type="number" min="1" step="1" value="1" inputmode="numeric">
      </td>
      <td class="quick-purchase__remove-cell">
        <button class="quick-purchase__remove" type="button" data-remove-row aria-label="この行を削除">×</button>
      </td>
    `;

    const input = row.querySelector('[data-search-input]');

    const updateSuggestions = async () => {
      resetRow(row);

      const currentValue = input.value;
      const matches = await search(currentValue);

      if (
        input.value === currentValue
        && document.activeElement === input
        && !row.dataset.variantId
      ) {
        showSuggestions(row, matches);
      }
    };

    input.addEventListener('input', updateSuggestions);

    input.addEventListener('focus', () => {
      if (!row.dataset.variantId) updateSuggestions();
    });

    input.addEventListener('blur', () => {
      setTimeout(() => closeSuggestions(row), 150);
    });

    row.querySelector('[data-quantity]').addEventListener('input', () => {
      updatePrice(row);
    });

    row.querySelector('[data-remove-row]').addEventListener('click', () => {
      if (rows.children.length === 1) {
        input.value = '';
        resetRow(row);
        return;
      }

      row.remove();
    });

    rows.append(row);
  };

  for (let index = 0; index < 10; index += 1) {
    createRow();
  }

  addRowButton.addEventListener('click', () => createRow());

  addToCartButton.addEventListener('click', async () => {
    const selectedRows = [...rows.children].filter((row) => row.dataset.variantId);

    if (!selectedRows.length) {
      setMessage('カートに追加する商品を選択してください。', 'error');
      return;
    }

    if (selectedRows.find((row) => row.dataset.available !== 'true')) {
      setMessage('売り切れの商品が含まれています。該当行を変更してください。', 'error');
      return;
    }

    const items = selectedRows.reverse().map((row) => ({
      id: Number(row.dataset.variantId),
      quantity: Math.max(
        1,
        Number(row.querySelector('[data-quantity]').value) || 1
      ),
    }));

    addToCartButton.disabled = true;
    setMessage('カートに追加しています…');

    try {
      const response = await fetch(root.dataset.cartUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.description || 'カートへの追加に失敗しました。');
      }

      setMessage('カートに追加しました。カートへ移動します。', 'success');
      sessionStorage.setItem(resetAfterCartKey, '1');
      window.location.assign(root.dataset.cartPageUrl);
    } catch (error) {
      setMessage(error.message || 'カートへの追加に失敗しました。', 'error');
      addToCartButton.disabled = false;
    }
  });
})();